import useOptimisticDeleteProposalLineItem from '@/api/mutations/proposals/optimistic/useOptimisticDeleteProposalLineItem';
import useOptimisticMoveProposalGroupOrLineItem from '@/api/mutations/proposals/optimistic/useOptimisticMoveProposalGroupOrLineItem';
import useOptimisticSetProposalGroupChildSelected from '@/api/mutations/proposals/optimistic/useOptimisticSetProposalGroupChildSelected';
import useOptimisticSetProposalLineItemQuantity from '@/api/mutations/proposals/optimistic/useOptimisticSetProposalLineItemQuantity';
import useCreateProposalGroup, {
  appendedGroupKey,
} from '@/api/mutations/proposals/useCreateProposalGroup';
import useDeleteProposalGroup from '@/api/mutations/proposals/useDeleteProposalGroup';
import { ProposalItemGroupSectionQuery } from '@/api/queries/proposal/useGetProposal';
import LineItemRow, {
  LineItemRowProps,
} from '@/ui/components/lineItemRow/LineItemRow';
import SelectItemGroupModal from '@/ui/components/selectItemGroupModal/SelectItemGroupModal';
import LineItemGroupRow from '@/ui/pages/proposalBuilderPage/components/LineItemGroupRow';
import ProposalTreeDragPreview from '@/ui/pages/proposalBuilderPage/components/ProposalTreeDragPreview';
import {
  canDropProposalNode,
  ProposalContainer,
  ProposalItem,
  proposalItemGroupSectionToTree,
  proposalNodeLabel,
  proposalTreeIsUnstarted,
} from '@/ui/pages/proposalBuilderPage/util';
import { AnchorButton, Button, Text } from '@payaca/ambrosia';
import { SortableTree } from '@payaca/dnd';
import { FC, ReactNode, useMemo, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

export type TProps = {
  proposalId: string;
  section: ProposalItemGroupSectionQuery;
};

const ProposalItemGroupTab: FC<TProps> = (props) => {
  const { proposalId, section } = props;

  const { url } = useRouteMatch();
  const history = useHistory();

  const { mutate: moveProposalGroupOrLineItem } =
    useOptimisticMoveProposalGroupOrLineItem({ proposalId });
  const { mutate: createProposalGroup, isPending: isCreatingGroup } =
    useCreateProposalGroup({ proposalId });
  // Deliberately not the optimistic delete: chained onto the create, its
  // `cancelQueries` reverts the create's own in-flight refetch, so the cache it
  // edits predates the group just added and the section loses both.
  const { mutate: deleteProposalGroup } = useDeleteProposalGroup({
    proposalId,
  });

  const [showItemGroupModal, setShowItemGroupModal] = useState(false);

  const [collapsedIds, setCollapsedIds] = useState<string[]>([]);

  const treeItems = useMemo(() => {
    return proposalItemGroupSectionToTree(section);
  }, [section]);

  const isUnstarted = proposalTreeIsUnstarted(treeItems);
  const seededGroupKey = isUnstarted ? treeItems[0]?.id.toString() : undefined;

  const createGroupAndAddItem = () => {
    createProposalGroup(
      {
        proposalId,
        parent: { sectionKey: section.key },
        source: { new: { selectionMode: 'REQUIRED' } },
      },
      {
        onSuccess: (data) => {
          const createdGroupKey = appendedGroupKey(data, section.key);
          if (createdGroupKey) {
            history.push(`${url}/${createdGroupKey}/add-item`);
          }
        },
      }
    );
  };

  const addSavedGroup = (lineItemGroupId: string) => {
    createProposalGroup(
      {
        proposalId,
        // A saved Item Group can only be added at the section's top level
        // (the backend rejects nesting it).
        parent: { sectionKey: section.key },
        source: { lineItemGroupId },
      },
      {
        // Left in place, the untitled empty group the section was hiding
        // surfaces beside the saved one, warning it is empty.
        onSuccess: () => {
          if (seededGroupKey) {
            deleteProposalGroup({ proposalId, groupKey: seededGroupKey });
          }
        },
      }
    );
    setShowItemGroupModal(false);
  };

  return (
    <>
      {isUnstarted ? (
        <ItemsEmptyState
          addItemTo={
            seededGroupKey ? `${url}/${seededGroupKey}/add-item` : undefined
          }
          isCreatingGroup={isCreatingGroup}
          onAddFirstItem={createGroupAndAddItem}
          onAddSavedGroup={() => setShowItemGroupModal(true)}
        />
      ) : (
        <>
          <SortableTree<ProposalItem, ProposalContainer>
            className="px-2.5 space-y-px"
            items={treeItems}
            canDrop={canDropProposalNode}
            collapsedIds={collapsedIds}
            onToggleCollapse={(id) =>
              setCollapsedIds((prev) => {
                const key = String(id);
                return prev.includes(key)
                  ? prev.filter((existing) => existing !== key)
                  : [...prev, key];
              })
            }
            onChange={(_next, move) => {
              moveProposalGroupOrLineItem({
                proposalId,
                child:
                  move.node.type === 'container'
                    ? { groupKey: String(move.node.id) }
                    : { proposalLineItemId: String(move.node.id) },
                target:
                  move.parentId == null
                    ? { sectionKey: section.key }
                    : { groupKey: String(move.parentId) },
                position: move.index,
              });
            }}
            getNodeLabel={proposalNodeLabel}
            renderDragOverlay={(node) => (
              <ProposalTreeDragPreview node={node} />
            )}
            renderItem={(
              node,
              { depth, collapsed, onToggleCollapse, parent }
            ) =>
              node.type === 'container' ? (
                <LineItemGroupRow
                  key={node.id}
                  proposalId={proposalId}
                  id={node.id.toString()}
                  title={node.title}
                  depth={depth}
                  addItemTo={`${url}/${node.id}/add-item`}
                  selectionMode={node.selectionMode}
                  collapsed={collapsed}
                  onToggleCollapse={onToggleCollapse}
                  hasMultiChoiceSubGroup={node.hasMultiChoiceSubGroup}
                  hasOptionalSubGroup={node.hasOptionalSubGroup}
                />
              ) : (
                <ProposalLineItemRowWithMutation
                  proposalId={proposalId}
                  key={node.proposalLineItem.publicId}
                  // The row's id carries the publicId: it must match the tree
                  // node id for drag-and-drop, and follow-up mutations and the
                  // editor route address rows by publicId, which — unlike the
                  // numeric id — is already known for an optimistically
                  // inserted row.
                  lineItem={{
                    ...node.proposalLineItem,
                    id: node.proposalLineItem.publicId,
                  }}
                  unitPriceExcTax={node.proposalLineItem.price.unitPriceExcTax}
                  totalIncTax={node.proposalLineItem.totals.totalValue}
                  discountAmount={node.proposalLineItem.totals.discountValue}
                  to={`${url}/${node.proposalLineItem.publicId}`}
                  selectionMode={parent?.selectionMode}
                  parentId={parent?.id}
                  selected={node.selected ?? false}
                />
              )
            }
          />

          <GroupActions
            disabled={isCreatingGroup}
            onAddGroup={() =>
              createProposalGroup({
                proposalId,
                parent: { sectionKey: section.key },
                source: { new: { selectionMode: 'REQUIRED' } },
              })
            }
            onAddSavedGroup={() => setShowItemGroupModal(true)}
          />
        </>
      )}

      <SelectItemGroupModal
        isOpen={showItemGroupModal}
        onClose={() => setShowItemGroupModal(false)}
        onSelectItemGroup={(group) => addSavedGroup(String(group.id))}
      />
    </>
  );
};

const ItemsEmptyState: FC<{
  /**
   * Where "Add item" leads. Absent on a Proposal carrying no groups at all —
   * one converted from an Estimate — where a group has to be created first, so
   * the control becomes a button and {@link onAddFirstItem} does the work.
   */
  addItemTo?: string;
  isCreatingGroup: boolean;
  onAddFirstItem: () => void;
  onAddSavedGroup: () => void;
}> = (props) => {
  const { addItemTo, isCreatingGroup, onAddFirstItem, onAddSavedGroup } = props;

  return (
    <div className="flex flex-col items-center gap-4 px-3.5 py-16">
      <Text>Start by adding your first item</Text>

      {addItemTo ? (
        <AnchorButton as={Link} to={addItemTo} size="sm" variant="secondary">
          Add item
        </AnchorButton>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          disabled={isCreatingGroup}
          isProcessing={isCreatingGroup}
          onClick={onAddFirstItem}
        >
          Add item
        </Button>
      )}

      <Text level="supporting">
        or{' '}
        <GroupActionLink disabled={isCreatingGroup} onClick={onAddSavedGroup}>
          add a saved group
        </GroupActionLink>
      </Text>
    </div>
  );
};

const GroupActions: FC<{
  disabled: boolean;
  onAddGroup: () => void;
  onAddSavedGroup: () => void;
}> = (props) => {
  const { disabled, onAddGroup, onAddSavedGroup } = props;

  return (
    <div className="flex w-full flex-col items-center p-3.5">
      <Text level="supporting">
        <GroupActionLink disabled={disabled} onClick={onAddGroup}>
          Add group
        </GroupActionLink>
        {' · '}
        <GroupActionLink disabled={disabled} onClick={onAddSavedGroup}>
          Add saved group
        </GroupActionLink>
      </Text>
    </div>
  );
};

/**
 * A group action rendered inline in supporting copy. `size="sm"` is what puts an
 * inline link Button at the supporting type size, matching the text around it.
 */
const GroupActionLink: FC<{
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}> = (props) => {
  const { disabled, onClick, children } = props;

  return (
    <Button
      variant="link"
      linkColour="grey"
      size="sm"
      className="underline"
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

const ProposalLineItemRowWithMutation: FC<
  LineItemRowProps & { proposalId: string }
> = (props) => {
  const { proposalId, ...rest } = props;

  const { mutate: setProposalGroupChildSelected } =
    useOptimisticSetProposalGroupChildSelected({ proposalId });

  const { mutate: setProposalLineItemQuantity } =
    useOptimisticSetProposalLineItemQuantity({ proposalId });

  const { mutate: deleteProposalLineItem } =
    useOptimisticDeleteProposalLineItem({ proposalId });

  return (
    <LineItemRow
      {...rest}
      onSelectedChange={(selected) =>
        setProposalGroupChildSelected({
          proposalId,
          child: { proposalLineItemId: rest.lineItem.id },
          selected,
        })
      }
      onQuantityChange={(newQuantity) => {
        if (newQuantity == null) return;
        setProposalLineItemQuantity({
          proposalId,
          proposalLineItemId: rest.lineItem.id,
          quantity: newQuantity,
        });
      }}
      onDelete={() =>
        deleteProposalLineItem({ proposalLineItemId: rest.lineItem.id })
      }
    />
  );
};

export default ProposalItemGroupTab;
